<template>
  <div class="okr-list">
    <Toolbar
      :okr-list-id="okrListId"
      :can-edit="canEdit"
      :can-publish="canPublish"
      :weight-alarm="weightAlarm"
      :organizational-skills-or-personal-development-warn="organizationalSkillsOrPersonalDevelopmentWarn"
      :is-performance-leader="viewedUser.isPerformanceLeader"
      :approval-status="approvalStatus"
      @okr-published="getOkr"
      @setWeightAlarmStyleTrue="weightAlarmStyle = true"
    />
    <LoadingSkeleton
      v-if="isLoading"
      :style="{ paddingTop: '8px' }"
    />
    <template v-else>
      <my-collapse :items="collapseItems">
        <template #performanceObjective>
          <div
            v-if="objectives.length > 0"
            id="okr-list"
            class="objective-card-list"
          >
            <ObjectiveCard
              v-for="(objective, index) in objectives"
              :key="objective.id"
              :columns="objective.columns"
              :data="objective.data"
              :objective="objective.rawData"
              :can-edit="canEdit"
              :can-review="canReview"
              :objective-index="index"
              :weight-alarm-style="weightAlarmStyle"
              :aligned-from-ids-change-type="alignedFromIdsChangeType"
              :user-id="userId"
              :is-my-management-scope="viewedUser.isMyManagementScope"
              @create-key-result="onCreateKeyResult(objective.id, index)"
              @edit-objective="(...args) => onEditObjective(...args, index)"
              @edit-key-result="
                (...args) => onEditKeyResult(...args, index, objective.id)
              "
              @objective-deleted="onDeleteObjective(index)"
              @key-result-deleted="
                keyResultIndex => onDeleteKeyResult(index, keyResultIndex)
              "
              @progress-updated="onProgressUpdated(index, objective.id)"
              @weight-updated="canPublish = true"
              @set-weight-alarm-style-false="weightAlarmStyle = false"
              @review-saved="onObjectiveReviewSaved(objective.id, index)"
              @aligned="
                updatedObjective => onAlignedToOther(updatedObjective, index)
              "
              @objective-invited="
                updatedObjective => onObjectiveInvited(updatedObjective, index)
              "
              @key-result-invited="
                (updatedKeyResult, keyResultIndex) =>
                  onKeyResultInvited(updatedKeyResult, index, keyResultIndex)
              "
              @objective-aligned-myself="
                updatedObjective =>
                  onObjectiveAlignedMyself(updatedObjective, index)
              "
              @key-result-aligned-myself="
                (updatedKeyResult, keyResultIndex) =>
                  onKeyResultAlignedMyself(
                    updatedKeyResult,
                    index,
                    keyResultIndex
                  )
              "
              @reorder="(...args) => onReorderKeyResult(...args, index)"
            />
            <div
              v-if="canEdit"
              class="button-container"
            >
              <CreateButton
                icon-name="icontianjia0"
                :text="$t('okr.list.createObjective')"
                :style="{ marginLeft: '9px', boxShadow: '0 0 6px 0 rgba(39, 52, 125, 0.06)' }"
                @click="onCreateObjective"
              />
            </div>
          </div>
          <EmptyOkrList
            v-else
            :can-edit="canEdit"
            @create-objective="onCreateObjective"
          />
        </template>
        <template #individualDevelopment>
          <div class="personal-objective-container">
            <div v-if="(viewedUser.isPerformanceLeader && organizationalSkills) || personalDevelopment">
              <personal-objective-card
                :can-edit="canEdit"
                :is-performance-leader="viewedUser.isPerformanceLeader"
                :organizational-skills="JSON.parse(organizationalSkills)"
                :personal-development="JSON.parse(personalDevelopment)"
                @create-objective="onEditPersonalObjective"
              />
              <personal-objective-review
                v-if="lockStatus"
                :organizational-skills-self-review="JSON.parse(organizationalSkillsSelfReview || '{}')"
                :personal-development-self-review="JSON.parse(personalDevelopmentSelfReview || '{}')"
                :is-performance-leader="viewedUser.isPerformanceLeader"
              />
            </div>
            <empty-personal-objective
              v-else
              :is-performance-leader="viewedUser.isPerformanceLeader"
              :can-edit="canEdit"
              @create-objective="onEditPersonalObjective"
            />
          </div>
        </template>
        <template #review>
          <OkrReviewCard
            v-if="okrListId"
            :okr-list-id="okrListId"
            :can-edit="canReview"
          />
        </template>
      </my-collapse>
    </template>
    <EditDrawer
      v-model:visible="isDrawerVisible"
      :editing-params="editingParams"
      @objective-created="onObjectiveCreated"
      @objective-saved="onObjectiveSaved"
      @key-result-created="onKeyResultCreated"
      @key-result-saved="onKeyResultSaved"
      @personal-objective-saved="onPersonalObjectiveSaved"
    />
  </div>
</template>

<script>
import map from 'lodash/map'
import storage from 'store'
import { ref, reactive, computed, watch, toRef, createVNode } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import { Modal, Checkbox } from 'ant-design-vue'
import Sortable from 'sortablejs'
import OkrReviewCard from './OkrReviewCard/index'

import {
  CREATE_OBJECTIVE,
  EDIT_OBJECTIVE,
  CREATE_KEY_RESULT,
  EDIT_KEY_RESULT,
  EDIT_PERSONAL_OBJECTIVE,
  EDIT_ORGANIZATIONAL_OBJECTIVE
} from '@/constants/okr-edit-type'
import { CHECK_CONDITION } from '@/store/mutation-types'
import useGuide from '@/composables/use-guide'
import { getOkrListIdByCycle } from '@/api/okr'
import {
  getPublishedOkr,
  getMyOkr,
  getMyObjective,
  reorderObjective,
  reorderKeyResult,
  getPersonalObjective,
  getOkrModifyStatus
} from '@/graphql/okr'
import useContent from '@/composables/use-content'
import LoadingSkeleton from './LoadingSkeleton'
import EmptyOkrList from './EmptyOkrList'
import CreateButton from '@/components/CreateButton'
import Toolbar from './Toolbar'
import ObjectiveCard from '@/components/ObjectiveCard'
import EditDrawer from './EditDrawer'
import { getDownloadUrl } from '@/utils/upload'
import PersonalObjectiveCard from './PersonalObjectiveCard.vue'
import MyCollapse from '@/components/MyCollapse'
import EmptyPersonalObjective from './EmptyPersonalObjective.vue'
import { getViewedUserInfo, getCollapseItems } from './viewed-user'
import { UNLOCKED } from '@/constants/lock-status.js'
import { WAITING_FOR_APPROVAL } from '@/constants/approval-status.js'
import PersonalObjectiveReview from './PersonalObjectiveReview.vue'
import moment from 'moment'
export default {
  name: 'OkrList',
  components: {
    EmptyOkrList,
    Toolbar,
    ObjectiveCard,
    EditDrawer,
    CreateButton,
    LoadingSkeleton,
    PersonalObjectiveCard,
    MyCollapse,
    EmptyPersonalObjective,
    OkrReviewCard,
    PersonalObjectiveReview
  },
  props: {
    userId: {
      type: Number,
      default: undefined
    }
  },
  setup (props) {
    useGuide()
    const store = useStore()
    const { t } = useI18n()
    const { highlightName } = useContent()
    const isLoading = ref(false)
    const okrListId = ref(null)
    const objectives = ref([])
    const editingParams = ref(null)
    const isDrawerVisible = ref(false)
    const selectedCycleId = computed(() => store.state.selectedCycleId)
    const lockStatus = computed(() => store.getters.lockStatus)
    const canReview = computed(() => typeof props.userId === 'undefined')
    const canEdit = computed(() => typeof props.userId === 'undefined' && approvalStatus.value !== WAITING_FOR_APPROVAL && lockStatus.value === UNLOCKED)
    const canPublish = ref(false)
    const approvalStatus = ref(undefined)

    const weightAlarmStyle = ref(false)
    const weightAlarm = computed(() => {
      return objectives.value.reduce((pre, cur) => pre + cur.rawData.weight, 0) !== 100
    })

    // 组织、个人发展
    const organizationalSkills = ref(undefined)
    const personalDevelopment = ref(undefined)
    const organizationalSkillsSelfReview = ref(undefined)
    const personalDevelopmentSelfReview = ref(undefined)

    const organizationalSkillsOrPersonalDevelopmentWarn = computed(() => {
      return !organizationalSkills.value && !personalDevelopment.value
    })

    const { user: viewedUser } = getViewedUserInfo(props.userId)

    const collapseItems = getCollapseItems(viewedUser)

    function makeObjectiveListSortable () {
      const elem = document.querySelector('#okr-list')
      Sortable.create(elem, {
        handle: '.objective-drag-icon',
        animation: 180,
        delay: 0,
        onEnd ({ newIndex, oldIndex }) {
          onReorderObjective(newIndex, oldIndex)
        }
      })
    }
    const alignedFromIdsChangeType = ref({})

    const alignedFromIdsAll = computed(() => {
      const arr = objectives.value.reduce((acc, cur) => {
        return acc.concat(cur.rawData.alignedFromIds).concat(cur.data.reduce((acckr, curkr) => {
          return acckr.concat(curkr.alignedFromIds)
        }, []))
        // return [1, 2]
      }, [])
      return [...new Set(arr)]
    })

    watch(alignedFromIdsAll, (val, pre) => {
      if (pre.length > val.length) {
        alignedFromIdsChangeType.value = '-'
      } else if (pre.length < val.length) {
        alignedFromIdsChangeType.value = '+'
      } else {
        alignedFromIdsChangeType.value = '='
      }
    })

    // 监听目标列表，判断是否应该显示新手指引
    watch(objectives, val => {
      if (val.length) {
        setTimeout(makeObjectiveListSortable)

        if (canEdit.value) {
          // 如果是我的 OKR 页面
          store.commit(CHECK_CONDITION, 'isMyObjectiveCreated')
        } else {
          // 如果是他人的 OKR 页面
          store.commit(CHECK_CONDITION, 'isOtherOkrObjectiveCreated')
        }
      }
    })

    onBeforeRouteLeave(
      () =>
        new Promise(resolve => {
          if (!canEdit.value || !canPublish.value) {
            return resolve()
          }

          const key = 'DoNotShowUnpublishedWarning'
          const value = storage.get(key)
          // 如果设置了不再提示，则直接跳转
          if (value) {
            resolve()
            return
          }

          let checked = false
          Modal.confirm({
            icon: createVNode(ExclamationCircleOutlined),
            title: t('okr.notPublishedWarning.title'),
            content: createVNode('div', { style: { marginTop: '16px' } }, [
              createVNode(
                Checkbox,
                {
                  onChange (e) {
                    checked = e.target.checked
                  }
                },
                () => t('okr.notPublishedWarning.notShowAgain')
              )
            ]),
            onOk: () => {
              if (checked) {
                storage.set(key, true)
              }
              return resolve()
            },
            onCancel: () => resolve(false)
          })
        })
    )

    function onReorderObjective (newIndex, oldIndex) {
      if (oldIndex === newIndex) {
        return
      }
      [objectives.value[newIndex], objectives.value[oldIndex]] = [
        objectives.value[oldIndex],
        objectives.value[newIndex]
      ]
      const objectiveIds = map(objectives.value, 'id')
      return reorderObjective(objectiveIds)
    }

    function onReorderKeyResult (newIndex, oldIndex, objectiveIndex) {
      if (oldIndex === newIndex) {
        return
      }

      const objective = objectives.value[objectiveIndex]
      ;[objective.data[newIndex], objective.data[oldIndex]] = [
        objective.data[oldIndex],
        objective.data[newIndex]
      ]
      const keyResultIds = map(objective.data, 'id')
      return reorderKeyResult(keyResultIds)
    }

    function getKeyResult (keyResult) {
      return {
        ...keyResult,
        content: highlightName(keyResult.content),
        alignedToUsers: getAlignedUsers(keyResult.linkToStatements),
        alignedFromUsers: getAlignedUsers(keyResult.linkedFromStatements),
        alignedToIds: map(keyResult.linkToStatements, 'id'),
        alignedFromIds: map(keyResult.linkedFromStatements, 'id'),
        key: keyResult.id
      }
    }

    function getReviewAttachment (attachment) {
      return {
        id: attachment.id,
        name: attachment.name,
        url: getDownloadUrl(attachment.id)
      }
    }

    function getObjective (objective, withKeyResult = true, index) {
      const data = withKeyResult
        ? objective.keyResults.map(keyResult => getKeyResult(keyResult))
        : objectives.value[index].data
      objective.content = highlightName(objective.content)
      objective.reviewMentionedIds = objective.reviewMentionUids
        ? objective.reviewMentionUids.split(',')
        : []
      objective.reviewMentionedIds = objective.reviewNextMentionUids
        ? objective.reviewNextMentionUids.split(',')
        : []
      objective.alignedToUsers = getAlignedUsers(objective.linkToStatements)
      objective.alignedFromUsers = getAlignedUsers(
        objective.linkedFromStatements
      )
      objective.alignedToIds = map(objective.linkToStatements, 'id')
      objective.alignedFromIds = map(objective.linkedFromStatements, 'id')
      objective.reviewAttachmentIds = objective.reviewAttachmentIds.map(
        getReviewAttachment
      )
      delete objective.keyResults // 删掉目标下的原始关键数据数组，以防后面出现和 data 数据不一致的问题
      const columns = [
        {
          dataIndex: 'icon',
          width: 51,
          slots: {
            title: 'objectiveIcon',
            customRender: 'icon'
          }
        },
        {
          dataIndex: 'content',
          slots: {
            title: 'objectiveContent',
            customRender: 'content'
          }
        },
        {
          dataIndex: 'weight',
          width: 120,
          slots: {
            title: 'objectiveWeight',
            customRender: 'weight'
          }
        },
        {
          key: 'alignment',
          width: 130,
          slots: {
            title: 'objectiveAlignment',
            customRender: 'keyResultAlignment'
          }
        },
        {
          dataIndex: 'progress',
          width: 120,
          slots: {
            title: 'objectiveProgress',
            customRender: 'progress'
          }
        },
        {
          key: 'comment',
          width: 90,
          slots: {
            title: 'objectiveComment',
            customRender: 'comment'
          }
        },
        canEdit.value && {
          key: 'action',
          width: 44,
          slots: {
            title: 'objectiveAction',
            customRender: 'keyResultAction'
          }
        }
      ].filter(Boolean)

      return {
        id: objective.id,
        rawData: objective,
        columns,
        data
      }
    }

    function getAlignedUsers (alignmentsRaw) {
      const alignmentObj = alignmentsRaw.reduce((obj, alignmentRaw) => {
        const user = alignmentRaw.createUid
        if (user) {
          const alignment = {
            id: alignmentRaw.id,
            content: alignmentRaw.content,
            type: alignmentRaw.relatedObjectiveId ? 'objective' : 'keyResult',
            objectiveId: alignmentRaw?.relatedObjectiveId?.id || undefined
          }

          if (obj[user.id]) {
            obj[user.id].okrItems.push(alignment)
          } else {
            obj[user.id] = {
              ...alignmentRaw.createUid,
              okrItems: [alignment]
            }
          }
        }
        return obj
      }, {})

      return Object.values(alignmentObj)
    }

    function getOkrByCycle (cycleId, userId) {
      isLoading.value = true
      objectives.value = []
      const getOkr =
        typeof props.userId === 'undefined' ? getMyOkr : getPublishedOkr
      getOkrListIdByCycle(cycleId, userId)
        .then(res => getOkr(res.id))
        .then(res => {
          okrListId.value = res.id
          objectives.value = res.objectives.map(objective =>
            getObjective(objective)
          )
          if (typeof props.userId === 'undefined') {
            // 如果当前周期的 OKR 还没有发布且没有任何值，则不允许发布
            if ((!res.isPublished && objectives.value.length === 0) || res.approvalStatus === 1) {
              canPublish.value = false
            } else {
              canPublish.value = res.isModified
            }
          }
          organizationalSkills.value = res.organizationalSkills
          personalDevelopment.value = res.personalDevelopment
          organizationalSkillsSelfReview.value = res.organizationalSkillsSelfReview
          personalDevelopmentSelfReview.value = res.personalDevelopmentSelfReview
          approvalStatus.value = res.approvalStatus
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    function getOkr () {
      getOkrByCycle(selectedCycleId.value, props.userId)
    }

    watch(selectedCycleId, cycleId => getOkrByCycle(cycleId, props.userId))

    if (selectedCycleId.value) {
      getOkr()
    }

    function onCreateObjective () {
      editingParams.value = reactive({
        type: CREATE_OBJECTIVE,
        iconName: 'iconmubiao',
        okrListId: okrListId.value
      })
      isDrawerVisible.value = true
    }

    function onEditObjective (objectiveId, objective, index) {
      editingParams.value = reactive({
        type: EDIT_OBJECTIVE,
        iconName: 'iconmubiao',
        objectiveId,
        data: objective,
        objectiveIndex: index
      })
      isDrawerVisible.value = true
    }

    function onCreateKeyResult (objectiveId, objectiveIndex) {
      editingParams.value = reactive({
        type: CREATE_KEY_RESULT,
        iconName: 'iconKR',
        objectiveId,
        objectiveIndex
      })
      isDrawerVisible.value = true
    }

    function onEditKeyResult (
      keyResult,
      keyResultIndex,
      objectiveIndex,
      objectiveId
    ) {
      editingParams.value = reactive({
        type: EDIT_KEY_RESULT,
        iconName: 'iconKR',
        data: {
          ...keyResult,
          startDay: keyResult.startDay ? moment.unix(keyResult.startDay) : undefined,
          finishDay: keyResult.finishDay ? moment.unix(keyResult.finishDay) : undefined,
          stateId: keyResult.stateId.id
        },
        objectiveId,
        objectiveIndex,
        keyResultIndex
      })
      isDrawerVisible.value = true
    }

    function onObjectiveCreated (objectiveId) {
      isDrawerVisible.value = false
      editingParams.value = null

      getMyObjective(objectiveId, true).then(objective => {
        objectives.value.push(getObjective(objective))
        canPublish.value = true
      })
    }

    function onObjectiveSaved () {
      const { objectiveId, objectiveIndex } = editingParams.value
      isDrawerVisible.value = false
      editingParams.value = null

      getMyObjective(objectiveId, true).then(objective => {
        objectives.value[objectiveIndex] = getObjective(objective)
        canPublish.value = true
      })
    }

    function onObjectiveReviewSaved (objectiveId, objectiveIndex) {
      getMyObjective(objectiveId, false).then(objective => {
        objectives.value[objectiveIndex] = getObjective(
          objective,
          false,
          objectiveIndex
        )
        canPublish.value = true
      })
    }

    function onKeyResultCreated () {
      const { objectiveIndex, objectiveId } = editingParams.value
      isDrawerVisible.value = false
      editingParams.value = null

      getMyObjective(objectiveId, true).then(objective => {
        objectives.value[objectiveIndex] = getObjective(objective)
        canPublish.value = true
      })
    }

    function onKeyResultSaved () {
      const { objectiveIndex, objectiveId } = editingParams.value
      isDrawerVisible.value = false
      editingParams.value = null

      getMyObjective(objectiveId, true).then(objective => {
        objectives.value[objectiveIndex] = getObjective(objective)
        getOkrModifyStatus(okrListId.value).then(res => {
          canPublish.value = res.isModified
        })
      })
    }

    function onDeleteObjective (index) {
      objectives.value.splice(index, 1)
      canPublish.value = true
    }

    function onDeleteKeyResult (objectiveIndex, keyResultIndex) {
      objectives.value[objectiveIndex].data.splice(keyResultIndex, 1)
      canPublish.value = true
    }

    function onAlignedToOther (updatedObjective, index) {
      const objective = toRef(objectives.value[index], 'rawData')
      objective.value.alignedToUsers = getAlignedUsers(
        updatedObjective.linkToStatements
      )
      objective.value.alignedToIds = map(
        updatedObjective.linkToStatements,
        'id'
      )
    }

    function onObjectiveInvited (updatedObjective, index) {
      const objective = toRef(objectives.value[index], 'rawData')
      objective.value.content = updatedObjective.content
      objective.value.mentionUserIds = updatedObjective.mentionUserIds
      canPublish.value = true
    }

    function onKeyResultInvited (
      updatedKeyResult,
      objectiveIndex,
      keyResultIndex
    ) {
      const keyResult = toRef(
        objectives.value[objectiveIndex].data,
        keyResultIndex
      )
      keyResult.value.content = updatedKeyResult.content
      keyResult.value.mentionUserIds = updatedKeyResult.mentionUserIds
      canPublish.value = true
    }

    function onKeyResultAlignedMyself (
      updatedKeyResult,
      objectiveIndex,
      keyResultIndex
    ) {
      const keyResult = toRef(
        objectives.value[objectiveIndex].data,
        keyResultIndex
      )
      keyResult.value.alignedFromUsers = getAlignedUsers(
        updatedKeyResult.linkedFromStatements
      )
      keyResult.value.alignedFromIds = map(
        updatedKeyResult.linkedFromStatements,
        'id'
      )
    }

    function onObjectiveAlignedMyself (updatedObjective, index) {
      const objective = toRef(objectives.value[index], 'rawData')
      objective.value.alignedFromUsers = getAlignedUsers(
        updatedObjective.linkedFromStatements
      )
      objective.value.alignedFromIds = map(
        updatedObjective.linkedFromStatements,
        'id'
      )
    }

    function onProgressUpdated (objectiveIndex, objectiveId) {
      getMyObjective(objectiveId, true).then(objective => {
        objectives.value[objectiveIndex] = getObjective(objective)
        // canPublish.value = true
      })
    }

    function onEditPersonalObjective () {
      editingParams.value = reactive({
        type: viewedUser.value.isPerformanceLeader
          ? EDIT_ORGANIZATIONAL_OBJECTIVE
          : EDIT_PERSONAL_OBJECTIVE,
        iconName: viewedUser.value.isPerformanceLeader
          ? 'iconzuzhi'
          : 'iconfazhan',
        okrListId: okrListId.value,
        data: {
          organizationalSkills: JSON.parse(organizationalSkills.value || '{}'),
          personalDevelopment: JSON.parse(personalDevelopment.value || '{}')
        }
      })
      isDrawerVisible.value = true
    }

    function onPersonalObjectiveSaved () {
      getPersonalObjective(okrListId.value).then(res => {
        organizationalSkills.value = res.organizationalSkills
        personalDevelopment.value = res.personalDevelopment
        isDrawerVisible.value = false
        canPublish.value = true
      })
    }

    return {
      isLoading,
      canEdit,
      canReview,
      canPublish,
      okrListId,
      objectives,
      isDrawerVisible,
      editingParams,
      onCreateObjective,
      onEditObjective,
      onCreateKeyResult,
      onEditKeyResult,
      getOkr,
      onAlignedToOther,
      onObjectiveInvited,
      onKeyResultInvited,
      onObjectiveAlignedMyself,
      onKeyResultAlignedMyself,
      onDeleteObjective,
      onDeleteKeyResult,
      onObjectiveCreated,
      onObjectiveSaved,
      onKeyResultCreated,
      onKeyResultSaved,
      onProgressUpdated,
      onReorderKeyResult,
      onObjectiveReviewSaved,
      onEditPersonalObjective,
      onPersonalObjectiveSaved,
      collapseItems,
      organizationalSkills,
      personalDevelopment,
      organizationalSkillsSelfReview,
      personalDevelopmentSelfReview,
      organizationalSkillsOrPersonalDevelopmentWarn,
      viewedUser,
      approvalStatus,
      weightAlarm,
      weightAlarmStyle,
      lockStatus,
      alignedFromIdsChangeType
    }
  }
}
</script>

<style lang="less" scoped>
.okr-list {
  margin-bottom: 8px;
}

.objective-card-list {
  display: flex;
  flex-direction: column;
  // padding-top: 8px;

  & > div {
    margin-bottom: 12px;
  }
}

.btn {
  padding: 6.4px 16px;
  margin-left: 16px;
  border: none;
}

.button-container {
  display: flex;
  align-items: center;
}

.personal-objective-container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 6px 0 rgba(39, 52, 125, 0.08);
}
</style>
